import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PROJECT_HEALTH_MAP, type AgencyOverview } from '@goyone/shared';
import { api } from '../../lib/api';
import { WORDS, date, num, plural } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { ErrorState, PageHead } from '../../components/ui';
import { Avatar } from '../../components/People';
import { useSession } from '../../auth/AuthProvider';
import { TaskDrawer } from './TaskDrawer';
import { ClientPreviewModal } from './Previews';

/**
 * لوحة إدارة المشاريع — الموقفُ كلُّه في شاشة.
 *
 * كانت أرقامًا مفردةً تقول «كم» ولا تقول «أين المشكلة». فصارت توزيعاتٍ
 * تُقرأ في نظرة: رحلةُ العميل من عقدٍ إلى باقة، وصحّةُ المشاريع، وآجالُ
 * ما هو مفتوح، وإيقاعُ الإنجاز ثمانيةَ أسابيع، وأثقلُ المشاريع، وحِملُ
 * الفريق، وخطوطُ العمل مقارنةً.
 *
 * وكلُّ رسمٍ بابٌ: النقرُ عليه يفتح الشاشةَ التي تُعالج ما يقوله. رسمٌ
 * يُقرأ ولا يُنقر يترك قارئَه يبحث عن الطريق بنفسه.
 */
export function AgencyHome() {
  const session = useSession();
  const [openTask, setOpenTask] = useState<string | null>(null);
  const [previewClient, setPreviewClient] = useState<string | null>(null);

  const overview = useQuery({
    queryKey: ['agency', 'overview'],
    queryFn: () => api<AgencyOverview>('/agency/overview'),
  });

  const d = overview.data;

  /* تحية أسانا الصباحية — باسم من فتح الشاشة لا عنوانٍ إداريّ. */
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'صباح الخير' : hour < 18 ? 'مساء النشاط' : 'مساء الخير';
  const firstName = session.user.name.split(' ')[0] ?? session.user.name;

  return (
    <div className="page">
      <PageHead
        title={`${greeting} يا ${firstName}`}
        subtitle="موقفُ المشاريع اليوم: ما وقّعناه، وما نعمل عليه، وما تأخّر، ومن يحمله"
      />

      {overview.isError && (
        <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
      )}
      {overview.isPending && <div className="skeleton" style={{ height: 260 }} />}

      {d && (
        <>
          {/*
            رحلةُ العميل — محطاتٌ متصلة لا بطاقاتٌ متناثرة: يوقّع فيُجهَّز،
            ثمّ يُدار عملُه اليوميّ، ثمّ يستقرّ على باقةٍ شهرية.

            وكلُّ محطّةٍ تفتح شاشةً **في هذا القسم**: العقودُ السارية تُقرأ
            في «العقود والتجهيز» بمشاريعها وتقدّمها، لا في شاشة عقود
            المبيعات — تلك تخدم من يكتب العقد لا من ينفّذه، وقذفُ المنفّذ
            إلى قسمٍ آخر يقطع عليه سياقَ عمله.
          */}
          <div className="ag-journey">
            <Link
              to="/agency/onboarding"
              className="ag-jstep"
              title="العقود السارية ومشاريعها"
            >
              <span className="ag-jicon">
                <Icon name="clipboard" size={17} />
              </span>
              <strong className="num">{d.contractsActive}</strong>
              <span className="ag-jlabel">عقد ساري</span>
            </Link>
            <span className="ag-jarrow" aria-hidden="true" />
            <Link
              to="/agency/onboarding"
              className={`ag-jstep${d.contractsWaiting > 0 ? ' is-alert' : ''}`}
            >
              <span className="ag-jicon">
                <Icon name="rocket" size={17} />
              </span>
              <strong className="num">{d.contractsWaiting}</strong>
              <span className="ag-jlabel">ينتظر مشروعه</span>
            </Link>
            <span className="ag-jarrow" aria-hidden="true" />
            <Link to="/agency/projects" className="ag-jstep">
              <span className="ag-jicon">
                <Icon name="briefcase" size={17} />
              </span>
              <strong className="num">{d.projectsActive}</strong>
              <span className="ag-jlabel">مشروع يعمل</span>
            </Link>
            <span className="ag-jarrow" aria-hidden="true" />
            <Link to="/agency/retainers" className="ag-jstep">
              <span className="ag-jicon">
                <Icon name="layers" size={17} />
              </span>
              <strong className="num">{d.retainersActive}</strong>
              <span className="ag-jlabel">باقة شهرية</span>
            </Link>
          </div>

          {/* ثلاثةُ رسومٍ تقرأ الموقف: صحّةٌ وآجالٌ وإيقاع */}
          <div className="ag-charts">
            <HealthDonut health={d.health} total={d.projectsActive} />
            <LoadBars load={d.load} open={d.tasksOpen} />
            <WeeklySpark weekly={d.weekly} />
          </div>

          {/* خطوطُ العمل — المحافظُ مقارنةً */}
          {d.portfolios.length > 0 && (
            <section className="card mt-4">
              <div className="card-head">
                <h2 className="card-title">
                  <Icon name="folder" size={15} /> خطوط العمل
                </h2>
                <Link to="/agency/portfolios" className="tiny">
                  كل المحافظ
                </Link>
              </div>
              <div className="card-body ag-pf-rows">
                {d.portfolios.map((p) => {
                  const pct = p.taskCount ? Math.round((p.doneCount / p.taskCount) * 100) : 0;
                  return (
                    <Link key={p.id} to="/agency/portfolios" className="ag-pf-row">
                      <span className="ag-dot" style={{ background: p.color }} />
                      <span className="ag-pf-name">{p.name}</span>
                      <span className="tiny mute num">{plural(p.projectCount, WORDS.project)}</span>
                      <span className="ag-progress" style={{ flex: 1 }}>
                        <span
                          className="ag-progress-fill"
                          style={{ width: `${pct}%`, background: p.color }}
                        />
                      </span>
                      <b className="num tiny">{pct}٪</b>
                      {p.overdueCount > 0 && (
                        <span className="ag-chip ag-chip-late num">{p.overdueCount} متأخّرة</span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          <div className="ag-home-cols mt-4">
            {/* أثقلُ المشاريع — أين يقف العمل */}
            <section className="card">
              <div className="card-head">
                <h2 className="card-title">
                  <Icon name="briefcase" size={15} /> مشاريع تحتاج نظرة
                </h2>
              </div>
              <div className="card-body">
                {d.busiestProjects.length === 0 ? (
                  <p className="mute small">لا مشاريع فيها مهامّ بعد.</p>
                ) : (
                  <ul className="ag-task-list">
                    {d.busiestProjects.map((p) => {
                      const pct = p.total ? Math.round((p.done / p.total) * 100) : 0;
                      return (
                        <li key={p.id}>
                          <Link
                            to={`/agency/projects/${p.id}`}
                            className="ag-task-row"
                            style={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            {p.accountName ? (
                              <Avatar name={p.accountName} src={p.accountAvatarUrl} size={26} />
                            ) : (
                              <span className="ag-dot" style={{ background: p.color }} />
                            )}
                            <span className="ag-task-title">
                              {p.name}
                              {p.accountName && (
                                <span className="tiny mute"> · {p.accountName}</span>
                              )}
                            </span>
                            {p.overdue > 0 && (
                              <span className="ag-chip ag-chip-late num">{p.overdue} متأخّرة</span>
                            )}
                            <span className="ag-progress" style={{ width: '6rem', flex: 'none' }}>
                              <span
                                className="ag-progress-fill"
                                style={{ width: `${pct}%`, background: p.color }}
                              />
                            </span>
                            <span className="tiny mute num">{p.open} مفتوحة</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </section>

            {/* حِملُ الفريق */}
            <section className="card">
              <div className="card-head">
                <h2 className="card-title">
                  <Icon name="users" size={15} /> حِمل الفريق
                </h2>
                <Link to="/agency/team" className="tiny">
                  كل الفريق
                </Link>
              </div>
              <div className="card-body">
                {d.teamLoad.length === 0 ? (
                  <p className="mute small">لا مهامّ مسنَدة الآن.</p>
                ) : (
                  <ul className="ag-task-list">
                    {d.teamLoad.map((m) => (
                      <li key={m.userId}>
                        <Link
                          to="/agency/team"
                          className="ag-task-row"
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          <Avatar name={m.name} src={m.avatarUrl} size={26} />
                          <span className="ag-task-title">{m.name}</span>
                          {m.overdue > 0 && (
                            <span className="ag-chip ag-chip-late num">{m.overdue} متأخّرة</span>
                          )}
                          <span className="tiny mute num">{m.open} مفتوحة</span>
                          <span className="tiny mute num">· {m.doneThisWeek} هذا الأسبوع</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </div>

          <div className="ag-home-cols mt-4">
            {/* المعالم القادمة */}
            <section className="card">
              <div className="card-head">
                <h2 className="card-title">
                  <Icon name="diamond" size={15} /> المعالم القادمة
                </h2>
              </div>
              <div className="card-body">
                {d.milestones.length === 0 ? (
                  <p className="mute small">
                    لا معالم مفتوحة. علِّم المهام الفارقة «معلمًا رئيسيًّا» من بطاقتها لتظهر هنا.
                  </p>
                ) : (
                  <ul className="ag-task-list">
                    {d.milestones.map((t) => (
                      <li key={t.id}>
                        <div className="ag-task-row" onClick={() => setOpenTask(t.id)}>
                          <Icon name="diamond" size={14} className="ag-milestone-ic" />
                          <span className="ag-task-title">{t.title}</span>
                          {t.projectName && (
                            <span className="ag-ctx-chip">
                              <span
                                className="ag-dot"
                                style={{ background: t.projectColor ?? undefined }}
                              />
                              {t.projectName}
                            </span>
                          )}
                          {t.dueAt && <span className="tiny mute num">{date(t.dueAt)}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            {/* المتأخرات */}
            <section className="card">
              <div className="card-head">
                <h2 className="card-title">
                  <Icon name="alert" size={15} /> أقدم المتأخرات
                </h2>
                <Link to="/agency/my" className="tiny">
                  مهامي
                </Link>
              </div>
              <div className="card-body">
                {d.overdue.length === 0 ? (
                  <p className="mute small">لا شيء متأخر — استمرّوا.</p>
                ) : (
                  <ul className="ag-task-list">
                    {d.overdue.map((t) => (
                      <li key={t.id}>
                        <div className="ag-task-row" onClick={() => setOpenTask(t.id)}>
                          <span className="ag-task-title">{t.title}</span>
                          {t.projectName && (
                            <span className="ag-ctx-chip">
                              <span
                                className="ag-dot"
                                style={{ background: t.projectColor ?? undefined }}
                              />
                              {t.projectName}
                            </span>
                          )}
                          {t.dueAt && <span className="tiny num ag-late-text">{date(t.dueAt)}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </div>

          {/* تجاوزات الباقات */}
          {d.retainersOver.length > 0 && (
            <section className="card mt-4">
              <div className="card-body">
                <h3 className="label mb-4 ag-late-text">
                  <Icon name="layers" size={13} /> باقات تجاوزت مسموحها هذه الدورة
                </h3>
                <ul className="ag-task-list">
                  {d.retainersOver.map((r) => (
                    <li key={r.id}>
                      <div className="ag-task-row">
                        <button
                          className="ag-task-title strong ag-linklike"
                          onClick={() => setPreviewClient(r.accountId)}
                        >
                          {r.accountName}
                        </button>
                        <span className="tiny mute">{r.packageName}</span>
                        {r.items
                          .filter((i) => i.over > 0)
                          .map((i) => (
                            <span key={i.serviceId} className="ag-chip ag-chip-late num">
                              {i.serviceName}: +{i.over} {i.unit}
                            </span>
                          ))}
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="tiny mute mt-2">
                  <Link to="/agency/retainers">صفحة الباقات</Link> فيها استهلاك كل العملاء.
                </p>
              </div>
            </section>
          )}
        </>
      )}

      {openTask && <TaskDrawer taskId={openTask} onClose={() => setOpenTask(null)} />}

      {previewClient && (
        <ClientPreviewModal accountId={previewClient} onClose={() => setPreviewClient(null)} />
      )}
    </div>
  );
}

/* ==========================================================================
   الرسوم — SVG خالصٌ بلا مكتبة، وكلٌّ منها بابٌ يُنقر
   ========================================================================== */

/**
 * قوسُ صحّة المشاريع.
 *
 * الحلقةُ تُظهر النسبةَ بالمساحة لا بالرقم: من يقرأ «٣ متعثّرة» لا يعرف
 * أهي ثلاثةٌ من أربعةٍ أم من أربعين. والحلقةُ تقولها قبل أن يُقرأ الرقم.
 */
function HealthDonut({
  health,
  total,
}: {
  health: AgencyOverview['health'];
  total: number;
}) {
  const parts = [
    { key: 'ON_TRACK', value: health.onTrack, color: 'var(--success)' },
    { key: 'AT_RISK', value: health.atRisk, color: 'var(--warning)' },
    { key: 'OFF_TRACK', value: health.offTrack, color: 'var(--danger)' },
    { key: 'unset', value: health.unset, color: 'var(--ink-4)' },
  ];
  const sum = parts.reduce((s, p) => s + p.value, 0) || 1;

  /* محيطُ الدائرة يُقسَّم بـ stroke-dasharray — لا مسارات مقوَّسة تُحسب. */
  const R = 46;
  const C = 2 * Math.PI * R;
  let offset = 0;

  return (
    <section className="card ag-chart">
      <div className="card-head">
        <h2 className="card-title">
          <Icon name="chart" size={15} /> صحّة المشاريع
        </h2>
      </div>
      <div className="card-body ag-donut-wrap">
        <svg viewBox="0 0 120 120" className="ag-donut" role="img" aria-label="توزيع صحّة المشاريع">
          <circle cx="60" cy="60" r={R} className="ag-donut-track" />
          {parts.map((p) => {
            if (p.value === 0) return null;
            const len = (p.value / sum) * C;
            const el = (
              <circle
                key={p.key}
                cx="60"
                cy="60"
                r={R}
                stroke={p.color}
                strokeDasharray={`${len} ${C - len}`}
                strokeDashoffset={-offset}
                className="ag-donut-seg"
              />
            );
            offset += len;
            return el;
          })}
          <text x="60" y="56" className="ag-donut-num">
            {num(total)}
          </text>
          <text x="60" y="74" className="ag-donut-cap">
            مشروعًا
          </text>
        </svg>
        <ul className="ag-legend">
          <LegendRow
            color="var(--success)"
            label={PROJECT_HEALTH_MAP.ON_TRACK.label}
            value={health.onTrack}
          />
          <LegendRow
            color="var(--warning)"
            label={PROJECT_HEALTH_MAP.AT_RISK.label}
            value={health.atRisk}
          />
          <LegendRow
            color="var(--danger)"
            label={PROJECT_HEALTH_MAP.OFF_TRACK.label}
            value={health.offTrack}
          />
          <LegendRow color="var(--ink-4)" label="بلا حالة معلنة" value={health.unset} />
        </ul>
      </div>
    </section>
  );
}

function LegendRow({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <li className="ag-legend-row">
      <span className="ag-dot" style={{ background: color }} />
      <span className="ag-legend-label">{label}</span>
      <b className="num">{value}</b>
    </li>
  );
}

/**
 * أعمدةُ الآجال — أين يقف الضغط.
 *
 * المهامُّ المفتوحة موزّعةً على آجالها: ما فات، واليوم، وهذا الأسبوع،
 * وبعده، وما بلا موعد. الأخيرُ يُقال ولا يُخفى: مهمّةٌ بلا موعدٍ عملٌ لن
 * يُذكِّر به أحد.
 */
function LoadBars({ load, open }: { load: AgencyOverview['load']; open: number }) {
  const bars = [
    { key: 'overdue', label: 'متأخّرة', value: load.overdue, tone: 'danger' },
    { key: 'today', label: 'اليوم', value: load.today, tone: 'warning' },
    { key: 'week', label: 'هذا الأسبوع', value: load.week, tone: 'brand' },
    { key: 'later', label: 'لاحقًا', value: load.later, tone: 'muted' },
    { key: 'noDate', label: 'بلا موعد', value: load.noDate, tone: 'muted' },
  ];
  const peak = Math.max(1, ...bars.map((b) => b.value));

  return (
    <section className="card ag-chart">
      <div className="card-head">
        <h2 className="card-title">
          <Icon name="clock" size={15} /> آجالُ المفتوح
        </h2>
        <Link to="/agency/my" className="tiny num">
          {open} مهمّة
        </Link>
      </div>
      <div className="card-body ag-bars">
        {bars.map((b) => (
          <div key={b.key} className={`ag-bar-row is-${b.tone}`}>
            <span className="ag-bar-label">{b.label}</span>
            <span className="ag-bar-track">
              <span className="ag-bar-fill" style={{ width: `${(b.value / peak) * 100}%` }} />
            </span>
            <b className="num ag-bar-num">{b.value}</b>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * إيقاعُ الإنجاز — ثمانيةُ أسابيع.
 *
 * الرقمُ الواحد يقول «أنجزنا ٤٠»؛ والخطُّ يقول «كنّا ننجز ٤٠ وصرنا ١٢».
 * والثاني هو الذي يُفتح له اجتماع.
 */
function WeeklySpark({ weekly }: { weekly: AgencyOverview['weekly'] }) {
  const peak = Math.max(1, ...weekly.map((w) => w.done));
  const last = weekly[weekly.length - 1]?.done ?? 0;
  const prev = weekly[weekly.length - 2]?.done ?? 0;
  const delta = last - prev;

  return (
    <section className="card ag-chart">
      <div className="card-head">
        <h2 className="card-title">
          <Icon name="chartBar" size={15} /> إيقاع الإنجاز
        </h2>
        <span className={`tiny num${delta < 0 ? ' ag-late-text' : ' mute'}`}>
          {delta === 0 ? 'كالأسبوع الماضي' : delta > 0 ? `+${delta} عن الماضي` : `${delta} عن الماضي`}
        </span>
      </div>
      <div className="card-body ag-spark">
        {weekly.map((w, i) => (
          <span
            key={w.weekStart}
            className={`ag-spark-col${i === weekly.length - 1 ? ' is-now' : ''}`}
            title={`أسبوع ${date(w.weekStart)}: ${w.done} مهمّة`}
          >
            <span className="ag-spark-fill" style={{ height: `${(w.done / peak) * 100}%` }} />
            <span className="ag-spark-cap num">{w.done}</span>
          </span>
        ))}
      </div>
    </section>
  );
}
